/**
 * Flood Report Handler - COMPLETE WITH ALL 11 REPORTS
 * Handles ALL flood reports matching backend tool names exactly
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import govtLogo from '../Govt_logo.png';
import moment from "moment";

/**
 * Load logo image
 */
const loadLogo = (src) =>
  new Promise((resolve, reject) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.src = src;
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
  });

/**
 * Format date to DD/MM/YYYY
 */
const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }
  if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) {
    return dateStr.replace(/-/g, '/');
  }
  if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    return dateStr;
  }
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Generate IST filename with date range
 */
const generateISTFilenameWithRange = (prefix, fromDate, toDate) => {
  const formatYYMMDD = (dateStr) => {
    const date = new Date(dateStr);
    const yy = String(date.getFullYear()).slice(-2);
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yy}${mm}${dd}`;
  };

  const now = new Date();
  const istTime = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
  const istTimeStr = istTime.toISOString().split('T')[1].split('.')[0].replace(/:/g, '');

  const fromFormatted = formatYYMMDD(fromDate);
  const toFormatted = formatYYMMDD(toDate);

  return `${prefix}_${fromFormatted}_${toFormatted}_${istTimeStr}`;
};

/**
 * Safe numeric value handler
 */
const getSafeNumericValue = (value) => {
  return (value !== null && value !== undefined) ? String(value) : '';
};

/**
 * Safe string value handler
 */
const getSafeStringValue = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return 'NA';
  }
  return String(value).trim();
};



/**
 * ========================================
 * CHECKER FUNCTIONS - MATCH BACKEND TOOL NAMES
 * ========================================
 */

/**
 * Check Flood Form 1 Daily Report
 * Backend: flood_form1_daily_report or floodform1dailyreport
 */
export const isFloodForm1DailyReport = (toolName) => {
  const floodReports = [
    'flood_form1_daily_report',
    'floodform1dailyreport',
    'flood_form_1_daily_report'
  ];
  return floodReports.includes(toolName?.toLowerCase());
};

/**
 * Check Flood Form 1 Report Status (Excel)
 * Backend: flood_form1_report_status or floodform1reportstatus
 */
export const isFloodForm1ReportStatus = (toolName) => {
  const statusReports = [
    'flood_form1_report_status',
    'floodform1reportstatus',
    'flood_form_1_report_status'
  ];
  return statusReports.includes(toolName?.toLowerCase());
};

/**
 * Check Flood Daily Consolidated Report
 * Backend: flood_daily_consolidated_report or flooddailyconsolidatedreport
 */
export const isFloodDailyConsolidatedReport = (toolName) => {
  const floodReports = [
    'flood_daily_consolidated_report',
    'flooddailyconsolidatedreport',
    'flood_consolidated_daily'
  ];
  return floodReports.includes(toolName?.toLowerCase());
};

/**
 * Check Flood Daily Partially Affected Report
 * Backend: flood_daily_partially_affected_report or flooddailypartiallyaffectedreport
 */
export const isFloodDailyPartiallyAffectedReport = (toolName) => {
  const floodReports = [
    'flood_daily_partially_affected_report',
    'flooddailypartiallyaffectedreport',
    'flood_partially_affected_daily'
  ];
  return floodReports.includes(toolName?.toLowerCase());
};

/**
 * Check Flood State Affected Report
 * Backend: flood_state_affected_report or floodstateaffectedreport
 */
export const isFloodStateAffectedReport = (toolName) => {
  const floodReports = [
    'flood_state_affected_report',
    'floodstateaffectedreport',
    'state_flood_affected'
  ];
  return floodReports.includes(toolName?.toLowerCase());
};

/**
 * Check Flood State Partially Affected Report
 * Backend: flood_state_partially_affected_report or floodstatepartiallyaffectedreport
 */
export const isFloodStatePartiallyAffectedReport = (toolName) => {
  const floodReports = [
    'flood_state_partially_affected_report',
    'floodstatepartiallyaffectedreport',
    'state_flood_partially_affected'
  ];
  return floodReports.includes(toolName?.toLowerCase());
};

/**
 * Check Flood State Consolidated Report
 * Backend: flood_state_consolidated_report or floodstateconsolidatedreport
 */
export const isFloodStateConsolidatedReport = (toolName) => {
  const floodReports = [
    'flood_state_consolidated_report',
    'floodstateconsolidatedreport',
    'state_flood_consolidated'
  ];
  return floodReports.includes(toolName?.toLowerCase());
};

/**
 * Check Flood MIS District
 * Backend: flood_mis_district or floodmisdistrict
 */
export const isFloodMISDistrict = (toolName) => {
  const misReports = [
    'flood_mis_district',
    'floodmisdistrict'
  ];
  return misReports.includes(toolName?.toLowerCase());
};

/**
 * Check Flood MIS District Datewise
 * Backend: flood_mis_district_datewise or floodmisdistrictdatewise
 */
export const isFloodMISDistrictDatewise = (toolName) => {
  const misReports = [
    'flood_mis_district_datewise',
    'floodmisdistrictdatewise'
  ];
  return misReports.includes(toolName?.toLowerCase());
};

/**
 * Check Flood MIS State
 * Backend: flood_mis_state or floodmisstate
 */
export const isFloodMISState = (toolName) => {
  const misReports = [
    'flood_mis_state',
    'floodmisstate'
  ];
  return misReports.includes(toolName?.toLowerCase());
};

/**
 * Check Flood MIS State Datewise
 * Backend: flood_mis_state_datewise or floodmisstatedatewise
 */
export const isFloodMISStateDatewise = (toolName) => {
  const misReports = [
    'flood_mis_state_datewise',
    'floodmisstatedatewise'
  ];
  return misReports.includes(toolName?.toLowerCase());
};

// NEW: Handle Flood Form1 Report Status Excel (Cold Wave Pattern)
export const handleFloodForm1ReportStatusExcel = async (message, format, rawData) => {
  console.log('Flood Form1 Report Status Excel generation started');

  if (format !== 'excel') {
    console.warn('Unsupported format for Flood Form1 status:', format);
    alert('Flood Form1 Report Status only supports Excel format');
    return;
  }

  try {
    // Extract data from backend response structure
    const data = rawData.data || rawData.statusrecords || rawData;
    const columnOrder = ['distname', 'totalblockcount', 'affected', 'partiallyaffected', 'notaffected', 'totalmarkedblocks'];
    const date = message.debug?.toolresult?.parameters?.date || message.debug?.parameters?.date || new Date().toISOString().split('T')[0];

    // Generate Excel using new Flood Form1 exporter
    const { generateFloodForm1Excel } = await import('../exportTableDataFlood.js');
    await generateFloodForm1Excel({
      data,
      columnOrder,
      toolName: 'floodform1reportstatus',
      date,
      filename: 'DailyFloodForm1Status'
    });

    console.log('Flood Form1 Excel downloaded successfully!');
  } catch (error) {
    console.error('Flood Form1 Excel generation failed:', error);
    alert('Excel Generation Error: ' + error.message);
  }
};

